/* eslint-disable camelcase */
import {
  ISubmitPayload, IFlowOptions, IContact, IAddress, AddressDetails,
} from 'types';
import { SMART_HR_ID } from '../app/constants';
import { CRMSimpleItem, CRMOptionSetValue } from '../options/crmTypes';

interface CRMPayRun {
  payCycle: number;
  employeeCount: number;
}

interface CRMContact {
  firstName: string;
  lastName: string;
  jobTitle: number;
  jobOther: string | null;
  email: string;
  contactNumber: string;
}

interface CRMAddress {
  isManual: boolean;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city?: CRMSimpleItem;
  region?: CRMSimpleItem;
  postCode: string;
  country: string;
}

interface CRMSignUpPayload {
  workItOut: {
    payruns: CRMPayRun[];
    managePayCycles: number | null;
    manageHR: number | null;
    bankChoice?: CRMSimpleItem;
    bankOther: string | null;
    otherProduct: CRMSimpleItem | null; // This is payroll.otherProduct
  };
  pickAPlan: {
    chosenPlan?: CRMSimpleItem;
    addSmartHr: boolean;
  },
  contactDetails: {
    isAccountant: boolean;
    isAdditionalPrimaryContact: boolean;
    contact?: CRMContact;
    additionalContact?: CRMContact;
  };
  companyDetails: {
    companyName?: string;
    tradingName?: string;
    irdNumber?: string;
    industryType?: CRMSimpleItem;
    businessAddress?: CRMAddress;
    postalAddress?: CRMAddress;
    postalSameAsBusiness: boolean;
    charityNumber: string | null;
    accountant?: CRMSimpleItem;
    accountantNotListed?: boolean;
    accountantBusinessName: string | null;
    accountantBusinessPhone: string | null;
    accountantHasAccessTo?: number;
    accountantName?: string;
    accountantWillPaySmartPayrollFees?: boolean;
    shouldAddToAccountantsUmbrellaPage?: boolean;
    umbrellaPageName: string | null;
  };
  payrollInformation: {
    bankAccountName: string;
    bankAccountNumber: string;
    authoriseDirectDebitMonthlyFees: boolean;
    notAuthorisedButRequestDirectDebitAuthority: boolean;
    processingPayroll: number | null;
    processingPayrollOther: string | null;
    payePaymentsToIRD: number | null;
    employeeAnnualLeaveRollover: number | null;
    averageGrossAmountPerPayRun: number;
    payrollProcessingStart: string;
  };
  summary: {
    additionalComments: string;
    howDidYouHearAboutUs?: CRMSimpleItem;
    howDidYouHearAboutUsOther: string | null;
    sendMeInfomation: boolean; // sendMeInformation
    promoCode?: CRMSimpleItem;
    referralCode: string;
  };
  requestId: string;
}

function getValueFromOptionSetValue(key: string, optionSetValue: CRMOptionSetValue[]): number | null {
  return optionSetValue.find(({ name }) => name === key)?.value || null;
}

interface TransformSignUpPayloadProps {
  data: ISubmitPayload;
  options: IFlowOptions;
  requestId: string;
  addressDetails?: AddressDetails;
}

export function transformSignUpPayload({
  data, options, requestId, addressDetails,
}: TransformSignUpPayloadProps): CRMSignUpPayload {
  const {
    workItOut,
    pickAPlan,
    // isCharity, Captured by the charity number
    promoCode,
    contactDetails,
    companyDetails,
    payrollInformation,
    summary,
  } = data;

  // console.log('🤔🤔🤔 transformSignUpPayload data', data);

  const payRuns: CRMPayRun[] = workItOut.payRuns.reduce<CRMPayRun[]>((accumulator, { payCycle, employeeCount }) => {
    if (payCycle !== undefined && employeeCount !== undefined) {
      const payCycleValue = options.payCycles.find(({ name }) => name === payCycle)?.value;
      if (!payCycleValue) {
        return accumulator;
      }

      return [
        ...accumulator,
        {
          payCycle: payCycleValue,
          employeeCount,
        },
      ];
    }

    return accumulator;
  }, []);

  const transformContact = (contact?: IContact): CRMContact | undefined => {
    if (!contact) {
      return undefined;
    }

    const jobTitle = options.jobTitles.find(({ name }) => name === contact.jobTitle) as CRMOptionSetValue;
    return {
      firstName: contact.firstName,
      lastName: contact.lastName,
      jobTitle: jobTitle.value,
      jobOther: contact.jobOther || null,
      email: contact.email,
      contactNumber: contact.contactNumber,
    };
  };

  const industryType = options.industryTypes.find(({ name }) => name === companyDetails.industryType);
  const accountantHasAccessTo = options.accountantHasAccessTo.find(({ name }) => name === companyDetails.accountantHasAccessTo)?.value;

  const transformAddress = (address?: IAddress): CRMAddress | undefined => {
    if (!address) {
      return undefined;
    }

    if (!address.isManual) {
      // Use the saved Google place/details object, if we have addressDetails it should always be for the place_id
      // however on some occasions the place predictions place_id doesn't match the details.place_id
      if (addressDetails) {
        // As per email chain "SmartPayroll Lead Webform Integration"
        // - Address Line 1 = route
        // - Address Line 2 = sublocality_level_1
        // - City = locality
        // - Postcode = postal_code
        // - Region = administrative_area_level_1
        // - Country = country

        // Use the google address lookup information
        const postCode = addressDetails.address_components.find(({ types }) => types.includes('postal_code'))?.long_name || '';
        const country = addressDetails.address_components.find(({ types }) => types.includes('country'))?.long_name || '';

        const detailsRegion = addressDetails.address_components.find(({ types }) => types.includes('administrative_area_level_1'))?.long_name || '';
        const region = options.regions.find(({ name }) => name === detailsRegion);

        const locality = addressDetails.address_components.find(({ types }) => types.includes('locality'))?.long_name || '';
        let city = options.cities.find(({ name }) => name === locality);

        // Fallback because administrative_area_level_2 isn't reliable and locality appears to be the city in most NZ cases.
        if (!city) {
          const detailsCity = addressDetails.address_components.find(({ types }) => types.includes('administrative_area_level_2'))?.long_name || '';
          city = options.cities.find(({ name }) => name === detailsCity);
        }

        if (!city) {
          // In the case where the Google details API doesn't include the city
          const potentialCities = (address?.address?.structured_formatting.secondary_text || '').split(',').map((potentialCityString) => potentialCityString.trim());
          const foundCity = options.cities.find(({ name }) => potentialCities.some((potentialCityString) => potentialCityString === name));
          city = foundCity;
        }

        const floor = addressDetails.address_components.find(({ types }) => types.includes('floor'))?.long_name || '';
        const streetNumber = addressDetails.address_components.find(({ types }) => types.includes('street_number'))?.long_name || '';
        const route = addressDetails.address_components.find(({ types }) => types.includes('route'))?.long_name || '';

        const addressLine2 = addressDetails.address_components.find(({ types }) => types.includes('sublocality_level_1'))?.long_name || '';

        let addressLine1 = '';
        if (floor) {
          addressLine1 += `${floor} `;
        }
        if (streetNumber) {
          addressLine1 += `${streetNumber} `;
        }
        if (route) {
          addressLine1 += `${route}`;
        }

        // If the Google details info doesn't include a number then use the places predictions endpoint
        const hasNumbers = /\d/.test(addressLine1);
        if (!hasNumbers) {
          // Which includes the initial streetNumber and route
          const streetNumberWithAddress = address?.address?.structured_formatting?.main_text;

          if (streetNumberWithAddress) {
            addressLine1 = streetNumberWithAddress;
          }
        }

        return {
          isManual: false,
          addressLine1,
          addressLine2,
          addressLine3: '',
          city,
          region,
          postCode,
          country,
        };
      }

      console.error('No details available for address, falling back to manual mode', address, addressDetails);

      return {
        isManual: true,
        addressLine1: address.addressLineOne || address.address?.description || '',
        addressLine2: address.addressLineTwo || '',
        addressLine3: address.addressLineThree || '',
        city: address.city,
        region: address.region,
        postCode: address.postCode || '',
        country: address.country || '',
      };
    }

    return {
      isManual: address.isManual || false,
      addressLine1: address.addressLineOne || address.address?.description || '',
      addressLine2: address.addressLineTwo || '',
      addressLine3: address.addressLineThree || '',
      city: address.city,
      region: address.region,
      postCode: address.postCode || '',
      country: address.country || '',
    };
  };

  const howDidYouHearAboutUs = options.leadSource.find(({ name }) => name === summary.howDidYouHearAboutUs);
  const managePayCycles: number | null = options.managePayCycleOptions.find(({ id }) => id === workItOut.managePayCycles)?.crmId || null;
  const manageHR: number | null = options.manageHrOptions.find(({ name }) => name === workItOut.manageHr)?.value || null;

  const planOption = options.plans.find(({ id }) => id === pickAPlan.chosenPlan);
  let chosenPlan;
  if (planOption) {
    chosenPlan = {
      id: planOption.crmId,
      name: planOption.title,
    };
  }

  let discountCode: CRMSimpleItem | undefined;
  if (promoCode) {
    discountCode = {
      id: promoCode.crmId,
      name: promoCode.code,
    };
  }

  const transformedBusinessAddress = transformAddress(companyDetails.businessAddress);
  const otherProduct: CRMSimpleItem | null = options.competingProducts.find(({ name }) => name === payrollInformation.otherProduct) || null;

  // This object matches what's in sign-up-request.
  return {
    workItOut: {
      payruns: payRuns,
      managePayCycles,
      manageHR,
      bankChoice: {
        id: workItOut.bankChoice?.id || '',
        name: workItOut.bankChoice?.name || '',
      },
      bankOther: workItOut.bankOther || null,
      otherProduct,
    },

    pickAPlan: {
      chosenPlan,
      addSmartHr: pickAPlan.extras.includes(SMART_HR_ID),
    },

    contactDetails: {
      isAccountant: contactDetails.isAccountant || false,
      isAdditionalPrimaryContact: contactDetails.isAdditionalPrimaryContact || false,
      contact: contactDetails.isAdditionalPrimaryContact ? transformContact(contactDetails.additionalContact) : transformContact(contactDetails.contact),
      additionalContact: contactDetails.isAdditionalPrimaryContact ? transformContact(contactDetails.contact) : transformContact(contactDetails.additionalContact),
    },

    companyDetails: {
      companyName: companyDetails.companyName,
      tradingName: companyDetails.tradingName,
      irdNumber: companyDetails.irdNumber,
      industryType,
      businessAddress: transformedBusinessAddress,
      postalAddress: companyDetails.postalSameAsBusiness ? transformedBusinessAddress : transformAddress(companyDetails.postalAddress),
      postalSameAsBusiness: companyDetails.postalSameAsBusiness || false,
      charityNumber: companyDetails.charityRegistrationNumber || null,
      accountant: companyDetails.accountant,
      accountantNotListed: companyDetails.accountantNotListed,
      accountantBusinessName: companyDetails.accountantBusinessName || null,
      accountantBusinessPhone: companyDetails.accountantPhoneNumber || null,
      accountantHasAccessTo,
      accountantName: companyDetails.accountantName,
      accountantWillPaySmartPayrollFees: companyDetails.accountantWillPaySmartPayrollFees,
      shouldAddToAccountantsUmbrellaPage: companyDetails.shouldAddToAccountantsUmbrellaPage,
      umbrellaPageName: companyDetails.umbrellaPageName || null,
    },

    payrollInformation: {
      bankAccountName: payrollInformation.bankAccountName,
      bankAccountNumber: payrollInformation.bankAccountNumber,
      authoriseDirectDebitMonthlyFees: payrollInformation.authoriseDirectDebitMonthlyFees,
      notAuthorisedButRequestDirectDebitAuthority: payrollInformation.notAuthorisedButRequestDirectDebitAuthority,
      processingPayroll: getValueFromOptionSetValue(payrollInformation.processingPayroll, options.payrollProcessingModes),
      processingPayrollOther: payrollInformation.processingPayrollOther || null,
      payePaymentsToIRD: getValueFromOptionSetValue(payrollInformation.payePaymentsToIrd, options.irdPaymentFrequencies),
      employeeAnnualLeaveRollover: getValueFromOptionSetValue(payrollInformation.employeeAnnualLeaveRollover, options.employeeLeaveRollover),
      averageGrossAmountPerPayRun: payrollInformation.averageGrossAmountPerPayRun,
      payrollProcessingStart: payrollInformation.payrollProcessingStart,
    },
    summary: {
      additionalComments: summary.additionalComments,
      howDidYouHearAboutUs,
      howDidYouHearAboutUsOther: summary.howDidYouHearAboutUsOther || null,
      sendMeInfomation: summary.sendMeInformation,
      promoCode: discountCode,
      referralCode: summary.referralCode,
    },
    requestId,
  };
}
